import React, { useState, useMemo, useEffect } from 'react';
import { CurrencyDollarIcon, ArrowTrendingUpIcon, ArrowTrendingDownIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { CurrencyDollarIcon as CurrencyDollarIconSolid } from '@heroicons/react/24/solid';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line, Area, AreaChart } from 'recharts';

interface FinanceStatusSectionProps {
  totalIncome?: number;
  totalExpense?: number;
  netIncome?: number;
  profitMargin?: string;
  monthlyBudget?: number;
  budgetUsed?: number;
}

// 색상 상수
const FINANCE_COLORS = ['#059669', '#DC2626', '#1D4ED8', '#F59E0B'];
const INCOME_COLORS = ['#10B981', '#3B82F6', '#8B5CF6', '#F59E0B'];
const EXPENSE_COLORS = ['#EF4444', '#F59E0B', '#EC4899', '#6B7280'];

export const FinanceStatusSection: React.FC<FinanceStatusSectionProps> = ({
  totalIncome = 2500000,
  totalExpense = 1800000,
  netIncome = 700000,
  profitMargin = "28",
  monthlyBudget = 2000000,
  budgetUsed = 1800000
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'income' | 'expense' | 'trends'>('overview');
  const [animatedValues, setAnimatedValues] = useState({
    totalIncome: 0,
    totalExpense: 0,
    netIncome: 0,
    budgetUsed: 0
  });

  // 애니메이션 효과
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedValues({
        totalIncome,
        totalExpense,
        netIncome,
        budgetUsed
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [totalIncome, totalExpense, netIncome, budgetUsed]);

  // 수입 구분 데이터
  const incomeData = useMemo(() => [
    {
      name: '십일조',
      value: Math.round(totalIncome * 0.6),
      color: '#10B981',
      percentage: 60,
      description: '교인들의 십일조 헌금'
    },
    {
      name: '감사헌금',
      value: Math.round(totalIncome * 0.24),
      color: '#3B82F6',
      percentage: 24,
      description: '감사 및 특별 헌금'
    },
    {
      name: '선교헌금',
      value: Math.round(totalIncome * 0.08),
      color: '#8B5CF6',
      percentage: 8,
      description: '선교 사역 후원금'
    },
    {
      name: '기타',
      value: Math.round(totalIncome * 0.08),
      color: '#F59E0B',
      percentage: 8,
      description: '기타 수입'
    }
  ], [totalIncome]);

  // 지출 구분 데이터
  const expenseData = useMemo(() => [
    {
      name: '사역비',
      value: Math.round(totalExpense * 0.44),
      color: '#EF4444',
      percentage: 44,
      description: '각종 사역 관련 지출'
    },
    {
      name: '시설운영비',
      value: Math.round(totalExpense * 0.28),
      color: '#F59E0B',
      percentage: 28,
      description: '전기, 수도, 관리비 등'
    },
    {
      name: '차량운영비',
      value: Math.round(totalExpense * 0.17),
      color: '#EC4899',
      percentage: 17,
      description: '교회 차량 관련 비용'
    },
    {
      name: '기타',
      value: Math.round(totalExpense * 0.11),
      color: '#6B7280',
      percentage: 11,
      description: '기타 운영비'
    }
  ], [totalExpense]);

  // 월별 재정 트렌드 데이터
  const trendData = useMemo(() => [
    { month: '1월', 수입: totalIncome - 300000, 지출: totalExpense - 200000, 순손익: netIncome - 100000 },
    { month: '2월', 수입: totalIncome - 200000, 지출: totalExpense - 150000, 순손익: netIncome - 50000 },
    { month: '3월', 수입: totalIncome - 100000, 지출: totalExpense - 100000, 순손익: netIncome },
    { month: '4월', 수입: totalIncome + 100000, 지출: totalExpense - 50000, 순손익: netIncome + 150000 },
    { month: '5월', 수입: totalIncome + 50000, 지출: totalExpense + 50000, 순손익: netIncome },
    { month: '6월', 수입: totalIncome, 지출: totalExpense, 순손익: netIncome }
  ], [totalIncome, totalExpense, netIncome]);

  // 예산 사용률 계산
  const budgetUtilization = monthlyBudget > 0 ? Math.round((budgetUsed / monthlyBudget) * 100) : 0;
  
  return (
    <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-2xl p-6 mb-8 shadow-lg border border-green-200 dark:border-green-800">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl">
            <CurrencyDollarIconSolid className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">재정관리 현황</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300">교회 재정 수입 및 지출 관리</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
            netIncome > 0 ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
            netIncome === 0 ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300' :
            'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
          }`}>
            <span className={`w-2 h-2 rounded-full mr-2 inline-block ${
              netIncome > 0 ? 'bg-green-500' :
              netIncome === 0 ? 'bg-blue-500' : 'bg-red-500'
            } animate-pulse`}></span>
            {netIncome > 0 ? '흑자' : netIncome === 0 ? '수지균형' : '적자'} {profitMargin}%
          </div>
        </div>
      </div>

      {/* 탭 네비게이션 */}
      <div className="flex flex-wrap gap-1 mb-6 bg-white dark:bg-gray-800 p-1 rounded-xl">
        <button
          onClick={() => setActiveTab('overview')}
          className={`flex-1 min-w-[100px] py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
            activeTab === 'overview'
              ? 'bg-green-500 text-white shadow-md'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          전체 현황
        </button>
        <button
          onClick={() => setActiveTab('income')}
          className={`flex-1 min-w-[100px] py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
            activeTab === 'income'
              ? 'bg-green-500 text-white shadow-md'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          수입 분석
        </button>
        <button
          onClick={() => setActiveTab('expense')}
          className={`flex-1 min-w-[100px] py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
            activeTab === 'expense'
              ? 'bg-green-500 text-white shadow-md'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          지출 분석
        </button>
        <button
          onClick={() => setActiveTab('trends')}
          className={`flex-1 min-w-[100px] py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
            activeTab === 'trends'
              ? 'bg-green-500 text-white shadow-md'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          재정 트렌드
        </button>
      </div>

      {/* 콘텐츠 영역 */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* 핵심 지표 카드 */}
        <div className="space-y-4">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">총 수입</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{animatedValues.totalIncome.toLocaleString()} HUF</p>
              </div>
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <ArrowTrendingUpIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">총 지출</p>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">{animatedValues.totalExpense.toLocaleString()} HUF</p>
              </div>
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <ArrowTrendingDownIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">순손익</p>
                <p className={`text-2xl font-bold ${netIncome >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-red-600 dark:text-red-400'}`}>
                  {netIncome >= 0 ? '+' : ''}{animatedValues.netIncome.toLocaleString()} HUF
                </p>
              </div>
              <div className={`p-2 rounded-lg ${netIncome >= 0 ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
                <ChartBarIcon className={`h-5 w-5 ${netIncome >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-red-600 dark:text-red-400'}`} />
              </div>
            </div>
          </div>
        </div>

        {/* 주요 내용 */}
        <div className="lg:col-span-3">
          {activeTab === 'overview' && (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">재정 전체 현황</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">수입/지출 비교</h4>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={[
                      { name: '수입', value: totalIncome, color: '#10B981' },
                      { name: '지출', value: totalExpense, color: '#EF4444' },
                      { name: '순손익', value: Math.abs(netIncome), color: netIncome >= 0 ? '#3B82F6' : '#DC2626' }
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(value) => [`${Number(value).toLocaleString()} HUF`, '금액']} />
                      <Bar dataKey="value" fill="#3B82F6" radius={[4, 4, 0, 0]}>
                        <Cell fill="#10B981" />
                        <Cell fill="#EF4444" />
                        <Cell fill={netIncome >= 0 ? '#3B82F6' : '#DC2626'} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">예산 사용 현황</h4>
                  <div className="space-y-4">
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm text-blue-600 dark:text-blue-400">월 예산</p>
                        <p className="text-lg font-bold text-blue-800 dark:text-blue-200">{monthlyBudget.toLocaleString()} HUF</p>
                      </div>
                      <div className="w-full bg-blue-200 dark:bg-blue-700 rounded-full h-2">
                        <div 
                          className="h-2 bg-blue-500 rounded-full transition-all duration-1000"
                          style={{ width: `${Math.min(budgetUtilization, 100)}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">사용률: {budgetUtilization}%</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                        <p className="text-sm text-green-600 dark:text-green-400">수익률</p>
                        <p className="text-xl font-bold text-green-800 dark:text-green-200">{profitMargin}%</p>
                      </div>
                      <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                        <p className="text-sm text-purple-600 dark:text-purple-400">운영 효율</p>
                        <p className="text-xl font-bold text-purple-800 dark:text-purple-200">우수</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'income' && (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">수입 분석</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">수입원별 분포</h4>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie 
                        data={incomeData} 
                        dataKey="value" 
                        nameKey="name" 
                        cx="50%" 
                        cy="50%" 
                        outerRadius={70} 
                        labelLine={false} 
                        label={{ fontSize: 12 }}
                      >
                        {incomeData.map((entry, index) => (
                          <Cell key={`cell-income-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [`${Number(value).toLocaleString()} HUF`, '수입']} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">수입원 상세</h4>
                  <div className="space-y-3">
                    {incomeData.map((item, index) => (
                      <div key={item.name} className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center space-x-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{item.name}</span>
                          </div>
                          <span className="text-sm font-bold text-gray-900 dark:text-white">
                            {item.value.toLocaleString()} HUF
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{item.description}</p>
                        <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                          <div 
                            className="h-2 rounded-full transition-all duration-1000" 
                            style={{ 
                              backgroundColor: item.color,
                              width: `${item.percentage}%`
                            }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'expense' && (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">지출 분석</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">지출 항목별 분포</h4>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie 
                        data={expenseData} 
                        dataKey="value" 
                        nameKey="name" 
                        cx="50%" 
                        cy="50%" 
                        outerRadius={70} 
                        labelLine={false} 
                        label={{ fontSize: 12 }}
                      >
                        {expenseData.map((entry, index) => (
                          <Cell key={`cell-expense-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [`${Number(value).toLocaleString()} HUF`, '지출']} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">지출 항목 상세</h4>
                  <div className="space-y-3">
                    {expenseData.map((item, index) => (
                      <div key={item.name} className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center space-x-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{item.name}</span>
                          </div>
                          <span className="text-sm font-bold text-gray-900 dark:text-white">
                            {item.value.toLocaleString()} HUF
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{item.description}</p>
                        <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                          <div 
                            className="h-2 rounded-full transition-all duration-1000" 
                            style={{ 
                              backgroundColor: item.color,
                              width: `${item.percentage}%`
                            }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'trends' && (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">재정 트렌드 분석</h3>
              
              <div className="mb-6">
                <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">6개월 재정 추이</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip formatter={(value) => [`${Number(value).toLocaleString()} HUF`, '']} />
                    <Legend />
                    <Line type="monotone" dataKey="수입" stroke="#10B981" strokeWidth={2} dot={{ r: 4 }} />
                    <Line type="monotone" dataKey="지출" stroke="#EF4444" strokeWidth={2} dot={{ r: 4 }} />
                    <Line type="monotone" dataKey="순손익" stroke="#3B82F6" strokeWidth={2} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-600 dark:text-green-400 font-medium">수입 증가율</p>
                      <p className="text-xl font-bold text-green-800 dark:text-green-200">+8.5%</p>
                    </div>
                    <ArrowTrendingUpIcon className="h-8 w-8 text-green-500" />
                  </div>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">재정 안정성</p>
                      <p className="text-xl font-bold text-blue-800 dark:text-blue-200">우수</p>
                    </div>
                    <ChartBarIcon className="h-8 w-8 text-blue-500" />
                  </div>
                </div>

                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 p-4 rounded-lg border border-emerald-200 dark:border-emerald-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">전망</p>
                      <p className="text-xl font-bold text-emerald-800 dark:text-emerald-200">매우 긍정적</p>
                    </div>
                    <CurrencyDollarIcon className="h-8 w-8 text-emerald-500" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};